'use client';

import { useEffect, useId, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Icon } from './icon';
import type { Tone } from './data';

/**
 * Shared primitives + hooks for the marketing site, ported from the design
 * handoff (`site/brand.jsx`): Logo/LogoMark, reveal-on-scroll, CountUp,
 * SectionHead, IconTile, and the tone→CSS-var map.
 */

/* ---- tone helper ---- */
export const TONE: Record<Tone, string> = {
  blue: 'var(--primary-2)',
  cyan: 'var(--cyan)',
  teal: 'var(--teal)',
  violet: 'var(--violet)',
  green: 'var(--green)',
  amber: 'var(--amber)',
  rose: 'var(--rose)',
};

export function toneVar(tone: Tone): string {
  return TONE[tone] ?? TONE.blue;
}

/* ---- Logo: gradient shield mark + FRAUDdefender wordmark ---- */
export function LogoMark({ size = 30 }: { size?: number }) {
  const id = useId();
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" style={{ flexShrink: 0 }} aria-hidden>
      <defs>
        <linearGradient id={id} x1="4" y1="2" x2="28" y2="30" gradientUnits="userSpaceOnUse">
          <stop stopColor="oklch(0.74 0.14 240)" />
          <stop offset="1" stopColor="oklch(0.52 0.18 262)" />
        </linearGradient>
      </defs>
      <path
        d="M16 2.3c2.7 2.1 6 3.4 9.2 3.6.9 0 1.6.8 1.6 1.7v8.1c0 6.8-4.6 10.3-10.2 12.2a1.6 1.6 0 0 1-1.1 0C9.9 28 5.2 24.5 5.2 17.7V7.6c0-.9.7-1.7 1.6-1.7C10 5.7 13.3 4.4 16 2.3Z"
        fill={`url(#${id})`}
      />
      <path d="M11.3 16.2l3.1 3.1 6.1-6.4" stroke="white" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function Logo({ size = 30, fontSize = 18 }: { size?: number; fontSize?: number }) {
  return (
    <span className="logo" style={{ fontSize }}>
      <LogoMark size={size} />
      <span className="logo-word">
        FRAUD<b>defender</b>
      </span>
    </span>
  );
}

/* ---- reveal-on-scroll: adds .in when an element enters the viewport ---- */
export function useReveal(): void {
  useEffect(() => {
    const els = Array.from(document.querySelectorAll('.reveal:not(.in)'));
    if (!('IntersectionObserver' in window) || els.length === 0) {
      els.forEach((e) => e.classList.add('in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add('in');
            io.unobserve(en.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
    );
    els.forEach((e) => io.observe(e));
    return () => io.disconnect();
  });
}

/** Staggered transition-delay for a container's `.reveal` children. */
export function rd(i: number, step = 70): CSSProperties {
  return { transitionDelay: `${i * step}ms` };
}

/* ---- count-up number when scrolled into view ---- */
export interface CountUpProps {
  to: number;
  from?: number;
  dur?: number;
  decimals?: number;
  prefix?: string;
  suffix?: string;
}

export function CountUp({ to, from = 0, dur = 1300, decimals = 0, prefix = '', suffix = '' }: CountUpProps) {
  const [val, setVal] = useState(from);
  const ref = useRef<HTMLSpanElement | null>(null);
  const done = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const run = () => {
      if (done.current) return;
      done.current = true;
      let start: number | undefined;
      const step = (t: number) => {
        if (!start) start = t;
        const p = Math.min(1, (t - start) / dur);
        const e = 1 - Math.pow(1 - p, 3);
        setVal(from + (to - from) * e);
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };
    if (!('IntersectionObserver' in window)) {
      run();
      return;
    }
    const io = new IntersectionObserver(
      (ents) =>
        ents.forEach((en) => {
          if (en.isIntersecting) {
            run();
            io.disconnect();
          }
        }),
      { threshold: 0.5 },
    );
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to]);
  return (
    <span ref={ref} className="tnum">
      {prefix}
      {val.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}
      {suffix}
    </span>
  );
}

/* ---- Section header (eyebrow + title + optional lead) ---- */
export interface SectionHeadProps {
  eyebrow?: string;
  title: ReactNode;
  lead?: ReactNode;
  align?: 'left' | 'center';
  max?: number;
}

export function SectionHead({ eyebrow, title, lead, align = 'left', max = 640 }: SectionHeadProps) {
  return (
    <div
      className="stack reveal"
      style={{
        gap: 18,
        alignItems: align === 'center' ? 'center' : 'flex-start',
        textAlign: align,
        maxWidth: max,
        marginInline: align === 'center' ? 'auto' : 0,
      }}
    >
      {eyebrow && <span className="eyebrow">{eyebrow}</span>}
      <h2 className="h2 balance" style={{ margin: 0 }}>
        {title}
      </h2>
      {lead && (
        <p className="lead pretty" style={{ margin: 0 }}>
          {lead}
        </p>
      )}
    </div>
  );
}

/* ---- Icon tile (square gradient-tinted icon holder) ---- */
export function IconTile({ name, tone = 'blue', size = 48, icon = 22 }: { name: string; tone?: Tone; size?: number; icon?: number }) {
  const c = toneVar(tone);
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: 13,
        display: 'grid',
        placeItems: 'center',
        color: c,
        flexShrink: 0,
        background: `linear-gradient(160deg, color-mix(in oklch, ${c} 22%, transparent), color-mix(in oklch, ${c} 6%, transparent))`,
        border: `1px solid color-mix(in oklch, ${c} 32%, transparent)`,
        boxShadow: `inset 0 1px 0 color-mix(in oklch, ${c} 24%, transparent)`,
      }}
    >
      <Icon name={name} size={icon} strokeWidth={1.8} />
    </span>
  );
}
